import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button
} from '@mui/material';
import {
  Image as ImageIcon,
  Launch as LaunchIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import useProjectService, { Project } from '../../../services/ProjectService';

/**
 * Project Gallery Page
 * Shows visual gallery of project deliverables, screenshots, and files
 */
const ProjectGallery: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { getProjects } = useProjectService();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projectsResult = await getProjects({ per_page: 20 });
        setProjects(projectsResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [getProjects]);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'on_hold': return 'warning';
      case 'cancelled': return 'error';
      case 'planning': return 'info';
      default: return 'default';
    }
  };

  // Mock project images for demonstration
  const getMockProjectImages = (projectId: number) => {
    const imageCount = Math.floor(Math.random() * 4) + 1; // 1-4 images
    return Array.from({ length: imageCount }, (_, index) => ({
      id: `${projectId}-${index}`,
      url: `https://picsum.photos/400/300?random=${projectId + index}`,
      title: `Project ${projectId} - Image ${index + 1}`,
      description: 'Project deliverable screenshot'
    }));
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading project gallery...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (projects.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Projects Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You don't have any projects to display in the gallery.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {projects.map((project) => {
        const projectImages = getMockProjectImages(project.id);
        
        return (
          <Card key={project.id} sx={{ mb: 4 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {project.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {project.description}
                  </Typography>
                </Box>
                <Chip 
                  label={project.status} 
                  color={getStatusColor(project.status)}
                  size="small"
                />
              </Stack>

              {projectImages.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  {projectImages.map((image) => (
                    <Card key={image.id} sx={{ position: 'relative', '&:hover .overlay': { opacity: 1 } }}>
                      <CardMedia
                        component="img"
                        height="150"
                        image={image.url}
                        alt={image.title}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedImage(image.url)}
                      />
                      <Box
                        className="overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          opacity: 0,
                          transition: 'opacity 0.3s'
                        }}
                      >
                        <IconButton
                          onClick={() => setSelectedImage(image.url)}
                          sx={{ color: 'white', mx: 1 }}
                        >
                          <FullscreenIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => window.open(image.url, '_blank')}
                          sx={{ color: 'white', mx: 1 }}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Box>
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="caption" display="block" noWrap>
                          {image.title}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <ImageIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No images available for this project
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {new Date(project.updated_at || project.created_at).toLocaleDateString()}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<LaunchIcon />}
                    onClick={() => window.location.href = `/dashboard/client/projects/${project.id}`}
                  >
                    View Project
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Project Image
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Project preview"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => selectedImage && window.open(selectedImage, '_blank')}>
            Open in New Tab
          </Button>
          <Button onClick={() => setSelectedImage(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectGallery;